import { createContext, useContext, useRef, useCallback, useEffect, ReactNode } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';

export type SoundEffect =
  | 'button-click'
  | 'auth-success'
  | 'auth-failure'
  | 'letter-send'
  | 'letter-open'
  | 'letter-close'
  | 'modal-close'
  | 'transition'
  | 'vo-welcome'
  | 'vo-instructions';

const SOUND_PATHS: Record<SoundEffect, string> = {
  'button-click': '/assets/sfx/ui-button-click.mp3',
  'auth-success': '/assets/sfx/ui-auth-success.mp3',
  'auth-failure': '/assets/sfx/ui-auth-failure.mp3',
  'letter-send': '/assets/sfx/ui-letter-send.mp3',
  'letter-open': '/assets/sfx/ui-letter-open.mp3',
  'letter-close': '/assets/sfx/ui-letter-close.mp3',
  'modal-close': '/assets/sfx/ui-modal-close.mp3',
  transition: '/assets/sfx/ui-transition.mp3',
  'vo-welcome': '/assets/sfx/vo-welcome.mp3',
  'vo-instructions': '/assets/sfx/vo-instructions.mp3',
};

const SOUND_VOLUMES: Record<SoundEffect, number> = {
  'button-click': 0.4,
  'auth-success': 0.4,
  'auth-failure': 0.4,
  'letter-send': 0.4,
  'letter-open': 0.4,
  'letter-close': 0.4,
  'modal-close': 0.4,
  transition: 0.4,
  'vo-welcome': 0.6,
  'vo-instructions': 0.6,
};

interface SoundContextValue {
  play: (effect: SoundEffect) => void;
}

const SoundContext = createContext<SoundContextValue | null>(null);

interface SoundProviderProps {
  children: ReactNode;
}

export function SoundProvider({ children }: SoundProviderProps) {
  const reducedMotion = useReducedMotion();
  const audioRefs = useRef<Map<SoundEffect, HTMLAudioElement>>(new Map());

  // Preload audio files
  useEffect(() => {
    if (reducedMotion) return;

    const refs = audioRefs.current;
    Object.entries(SOUND_PATHS).forEach(([effect, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      audio.volume = SOUND_VOLUMES[effect as SoundEffect];
      refs.set(effect as SoundEffect, audio);
    });

    return () => {
      refs.forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
      refs.clear();
    };
  }, [reducedMotion]);

  const play = useCallback(
    (effect: SoundEffect) => {
      if (reducedMotion) return;

      const audio = audioRefs.current.get(effect);
      if (audio) {
        // Reset to start for rapid replays
        audio.currentTime = 0;
        audio.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    },
    [reducedMotion],
  );

  return <SoundContext.Provider value={{ play }}>{children}</SoundContext.Provider>;
}

export function useSounds(): SoundContextValue {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSounds must be used within a SoundProvider');
  }
  return context;
}
